import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { modalSlice } from '../../store/reducers/ModalSlice';
import './OffersForYou.scss';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import { BannerActionTypes, IUserBanner } from '../../models/UserInfo';
import { GlobalRouteNames, ModalVariants, TopUpRouteNames } from '../../models/Common';
import { BasicButton } from '../../components/commons/buttons/BasicButton/BasicButton';
import { ReactComponent as SeaGull } from '../../assets/images/SeaGull.svg';
import { handleBannerAnalytics } from '../../utils';

export const OffersForYou: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const isDesktop = useMediaQuery('(min-width:1280px)');
  const isTablet = useMediaQuery('(min-width:768px)');
  const { data: bannersInfo, isFetching: isLoadingBanners } = userInfoApi.useGetUserBannersQuery();

  const handleOpenLink = (link?: string) => {
    if (link) window.open(link, '_blank');
  };

  const getDeviceKey = () => {
    if (isDesktop) return 'desktop';
    if (isTablet) return 'tablet';
    return 'mobile';
  };

  const handleAction = (path: string, search?: string) => {
    history.push({ pathname: path, search: search || '' });
  };

  const handleOpenCallbackModal = (serviceRequest: string, serviceName: string, bannerId: number) => {
    const site = `Сайт: ${process.env.REACT_APP_NAME || ''}.`;
    const agreement = `№ договора: ${userInfoState.id}.`;
    const address = `Домашний адрес: ул. ${userInfoState?.Addresses[0]?.Street?.NameRu} дом ${userInfoState?.Addresses[0]?.House}.`;
    dispatch(
      openModal({
        modalVariants: ModalVariants.Callback,
        content: {
          service: serviceName,
          title: t('changeService.serviceActivationRequest'),
          comment: `${site} ${serviceRequest} ${agreement} ${address}`,
          userId: bannerId,
        },
      }),
    );
  };

  const handleClick = (banner: IUserBanner) => {
    switch (banner.Action.Action) {
      case BannerActionTypes.OPEN_LINK: {
        handleBannerAnalytics(banner.id, BannerActionTypes.OPEN_LINK);
        handleOpenLink(banner.Action.Params.LinkUrl);
        break;
      }
      case BannerActionTypes.TOP_UP: {
        handleBannerAnalytics(banner.id, BannerActionTypes.TOP_UP);
        handleAction(
          `${GlobalRouteNames.TopUpBalancePage}${TopUpRouteNames.BankCard}`,
          banner.Action.Params.Amount
            ? `?amount=${banner.Action.Params.Amount}&bannerId=${banner.id}`
            : `?bannerId=${banner.id}`,
        );
        break;
      }
      case BannerActionTypes.CHANGE_SERVICE: {
        handleBannerAnalytics(banner.id, BannerActionTypes.CHANGE_SERVICE);
        if (banner.Action.Params.isAllowUserChange) {
          handleAction(
            `${
              banner.Action.Params.service?.Daily
                ? GlobalRouteNames.DailyServicePayment
                : GlobalRouteNames.ServicePayment
            }`,
            `?accountId=${banner.Action.Params.accountID}&accountType=${banner.Action.Params.serviceType}&serviceId=${banner.Action.Params.serviceId}&bannerId=${banner.id}&isChangeService=true`,
          );
        } else {
          handleOpenCallbackModal(
            `Запрос на подключение услуги: ${banner.Action.Params.service?.Name}`,
            `${banner.Action.Params.service?.Name}`,
            banner.id,
          );
        }
        break;
      }
      case BannerActionTypes.DO_PAYMENT: {
        handleBannerAnalytics(banner.id, BannerActionTypes.DO_PAYMENT);
        handleAction(
          `${
            banner.Action.Params.service?.Daily ? GlobalRouteNames.DailyServicePayment : GlobalRouteNames.ServicePayment
          }`,
          `?accountId=${banner.Action.Params.accountID}&accountType=${banner.Action.Params.serviceType}&bannerId=${
            banner.id
          }&serviceId=${banner.Action.Params.serviceId}${
            banner.Action.Params.PaymentCounts ? `&counts=${banner.Action.Params.PaymentCounts}` : ''
          }`,
        );
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="offers-page page">
      <div className="offers-page__container container">
        <div className="offers-cards-container">
          <div className="offers-cards-container__header">
            <strong className="offers-title">{t('newOffers.offersForYou')}</strong>
          </div>
          <div className="offers-cards-container__cards-container">
            {isLoadingBanners && (
              <>
                <div className="offers-cards-container__cards-container-skeleton skeleton" />
                <div className="offers-cards-container__cards-container-skeleton skeleton" />
              </>
            )}
            {!isLoadingBanners &&
              !!bannersInfo?.length &&
              bannersInfo?.map((banner, index) => (
                <BasicButton
                  key={banner.id}
                  className="offers-cards-container__offers-banner"
                  handleClick={() => handleClick(banner)}
                >
                  <img src={banner.Images?.[getDeviceKey()]?.filename} alt={`banner ${index + 1}`} />
                </BasicButton>
              ))}
            {!isLoadingBanners && !bannersInfo?.length && (
              <div className="offers-cards-container__offers-empty">
                <SeaGull className="seagull" />
                <p>{t('newOffers.empty')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
